/**
 * MOVIO V2 — business content.
 * The 3D layer consumes this data; it never owns copy, links or metrics.
 */

export const brand = {
  headline: ['You Know the Business.', 'We Build the Technology.'],
  support: 'Turn your established business into a technology-powered company.',
  statement:
    'MOVIO partners with established entrepreneurs to digitize, launch and scale mobility, logistics and service businesses with modern technology, AI and growth-aligned partnership models.',
};

export const partnerLinks = {
  /** Contact form preselects "White-label Partnership" for this query. */
  become: { label: 'Become a MOVIO Partner', href: '/contact?inquiry=partner' },
  explore: { label: 'Explore MOVIO', href: '#movio-idea' },
  tellUs: { label: 'Tell Us About Your Business', href: '/demo-booking' },
};

export const heroNodes = ['AI', 'Logistics', 'Mobility', 'Services', 'Customers'] as const;

/* -------------------------------------------------------------------------- */
/* The MOVIO idea                                                              */
/* -------------------------------------------------------------------------- */

export const ideaStory = {
  eyebrow: 'The MOVIO Idea',
  headline: ["We Don't Just Give You Software.", 'We Help You Build the Business.'],
  body: 'A software licence leaves you alone with the hardest part. MOVIO works as a technology partner: we build, launch and run the platform with you, and we grow when you grow.',
  oldWay: ['Traditional Software', 'Software', 'Business'],
  movioWay: ['Entrepreneur', 'MOVIO', 'Technology', 'Launch', 'Operate', 'Scale', 'Growth'],
};

export const partnershipModel = [
  {
    who: 'You bring',
    title: 'The business',
    points: ['Customers and local trust', 'Fleet, teams and operations', 'Market knowledge'],
  },
  {
    who: 'MOVIO brings',
    title: 'The technology',
    points: [
      'Branded apps, dispatch and payments',
      'AI voice and automation',
      'Launch, hosting and support',
    ],
  },
  {
    who: 'Together',
    title: 'Growth-aligned partnership',
    points: ['Pay-as-you-grow model', 'Shared focus on scale', 'Expansion into new cities'],
  },
];

/* -------------------------------------------------------------------------- */
/* Industries                                                                  */
/* -------------------------------------------------------------------------- */

export type IndustryId = 'mobility' | 'logistics' | 'ai-voice' | 'services' | 'agri' | 'future';

export interface Industry {
  id: IndustryId;
  index: string;
  title: string;
  short: string;
  audience: string;
  headline: string;
  description: string;
  steps: string[];
  href: string;
  cta: string;
  theme: 'light' | 'dark';
  note?: string;
}

export const industries: Industry[] = [
  {
    id: 'mobility',
    index: '01',
    title: 'MOVIO Mobility',
    short: 'Mobility',
    audience: 'Taxi, cab and fleet operators',
    headline: 'The infrastructure behind a modern ride network.',
    description:
      'Customer app, dispatch, driver app and payments under your own brand, running every trip from the first tap to the final payment.',
    steps: [
      'Customer Booking',
      'Dispatch',
      'Driver Assignment',
      'Vehicle Movement',
      'Pickup',
      'Trip',
      'Payment',
    ],
    href: '/products/ride-hailing',
    cta: 'Explore Mobility',
    theme: 'light',
  },
  {
    id: 'logistics',
    index: '02',
    title: 'MOVIO Logistics',
    short: 'Logistics',
    audience: 'Logistics, courier and delivery companies',
    headline: 'Every parcel, route and driver on one live network.',
    description:
      'Warehouse dispatch, route planning, driver apps and proof of delivery, so customers can follow every shipment in real time.',
    steps: ['Warehouse', 'Dispatch', 'Vehicle', 'Delivery Nodes', 'Customer'],
    href: '/products/logistics',
    cta: 'Explore Logistics',
    theme: 'light',
  },
  {
    id: 'ai-voice',
    index: '03',
    title: 'MOVIO AI Voice',
    short: 'AI Voice',
    audience: 'Call-centre driven businesses',
    headline: 'Customers call. AI books.',
    description:
      'An AI voice agent answers calls, understands the request, finds a vehicle and confirms the booking, day and night, in your customers’ language.',
    steps: ['“Book a taxi”', 'Understand', 'Search', 'Dispatch', 'Confirm', 'Booking Confirmed'],
    href: '/products/ai-call-center',
    cta: 'Explore AI Voice',
    theme: 'dark',
  },
  {
    id: 'services',
    index: '04',
    title: 'MOVIO Services',
    short: 'Local Services',
    audience: 'Electricians, plumbers, AC and appliance technicians',
    headline: 'Turn a trusted local service into an on-demand platform.',
    description:
      'Customers book online, the right technician is matched and tracked to the door, and the job is completed and paid for digitally.',
    steps: [
      'Customer',
      'Booking',
      'Service Matching',
      'Technician',
      'Location Tracking',
      'Service Completed',
      'Payment',
    ],
    href: '/products/facility-pro',
    cta: 'Explore Services',
    theme: 'light',
  },
  {
    id: 'agri',
    index: '05',
    title: 'MOVIO Agri',
    short: 'Agriculture',
    audience: 'Tractor and farm machinery owners',
    headline: 'Digital booking for physical machinery.',
    description:
      'Farmers book tractors and equipment from their phone. Owners see demand, assign machines and route them to the field.',
    steps: [
      'Farmer Request',
      'Booking Point',
      'Machine Assigned',
      'Route to Field',
      'Work Completed',
      'Payment',
    ],
    href: '/contact?inquiry=partner',
    cta: 'Talk to us about Agri',
    theme: 'light',
  },
  {
    id: 'future',
    index: '06',
    title: 'MOVIO Future Mobility',
    short: 'Future Mobility',
    audience: 'Operators planning for what comes next',
    headline: 'Ready for the Next Generation of Mobility',
    description:
      'The same booking, dispatch and payment foundation that runs road mobility today is designed to extend to new modes such as electric air taxis as they become commercially available.',
    steps: ['Road Mobility', 'Digital Infrastructure', 'Future Mobility', 'Air Taxi / eVTOL'],
    href: '/contact?inquiry=partner',
    cta: 'Plan for the future with MOVIO',
    theme: 'dark',
    note: 'Concept visualization. MOVIO does not currently operate air-taxi services; this shows how the platform is built to be future-ready.',
  },
];

export const industryById = Object.fromEntries(industries.map((i) => [i.id, i])) as Record<
  IndustryId,
  Industry
>;

/* -------------------------------------------------------------------------- */
/* Transformation                                                              */
/* -------------------------------------------------------------------------- */

export const transformation = {
  eyebrow: 'Transformation',
  headline: 'From Local Operator to Technology-Powered Company',
  before: [
    'Phone calls',
    'Paper registers',
    'Spreadsheets',
    'Manual dispatch',
    'Fragmented operations',
  ],
  after: [
    'Customer App',
    'Driver App',
    'Web Booking',
    'WhatsApp',
    'AI Voice',
    'Dispatch',
    'Payments',
    'Analytics',
    'Automation',
    'Business Intelligence',
  ],
  final: 'Technology-powered business ecosystem.',
};

/* -------------------------------------------------------------------------- */
/* Ecosystem                                                                   */
/* -------------------------------------------------------------------------- */

export interface EcosystemNode {
  id: string;
  label: string;
  description: string;
  href: string;
}

export const ecosystem: EcosystemNode[] = [
  {
    id: 'mobility',
    label: 'Mobility',
    description: 'Taxi, cab and fleet platforms with dispatch, driver apps and payments.',
    href: '/products/ride-hailing',
  },
  {
    id: 'logistics',
    label: 'Logistics',
    description: 'Courier, cargo and last-mile delivery with live tracking.',
    href: '/products/logistics',
  },
  {
    id: 'ai-voice',
    label: 'AI Voice',
    description: 'AI agents that answer calls and create bookings automatically.',
    href: '/products/ai-call-center',
  },
  {
    id: 'services',
    label: 'Service',
    description: 'On-demand home and facility services with technician matching.',
    href: '/products/facility-pro',
  },
  {
    id: 'agri',
    label: 'Agri',
    description: 'Booking and routing for tractors and farm machinery.',
    href: '#industry-agri',
  },
  {
    id: 'future',
    label: 'Future Mobility',
    description: 'A foundation designed to extend to new modes such as eVTOL.',
    href: '#industry-future',
  },
  {
    id: 'super-app',
    label: 'Super App',
    description: 'One customer app connecting every local business on the network.',
    href: '#super-app',
  },
];

/* -------------------------------------------------------------------------- */
/* Super app                                                                   */
/* -------------------------------------------------------------------------- */

export const superApp = {
  eyebrow: 'MOVIO Super App',
  headline: 'One App. Every Local Service.',
  body: 'Customers reach every business on the MOVIO network from a single app, and each booking flows to the local partner who fulfils it.',
  services: ['Ride', 'Delivery', 'Logistics', 'Home Services', 'Machinery'],
  flow: [
    'Customer',
    'MOVIO App',
    'Local Business',
    'Booking',
    'Fulfilment',
    'Payment',
    'Data',
    'Growth',
  ],
};

/* -------------------------------------------------------------------------- */
/* Tamil Nadu → Everywhere                                                     */
/* -------------------------------------------------------------------------- */

export const expansion = {
  eyebrow: 'Expansion',
  headline: ['Proven in Tamil Nadu.', 'Built for Everywhere.'],
  disclaimer:
    'Visualization of expansion capability. Only Tamil Nadu reflects current operations; other stages show where the platform is designed to go.',
  stages: [
    {
      id: 'tn',
      label: 'Tamil Nadu',
      status: 'Operating today',
      detail: '22+ cities running on MOVIO',
    },
    {
      id: 'regional',
      label: 'Regional markets',
      status: 'Expansion in progress',
      detail: 'Kerala, Karnataka and Chhattisgarh',
    },
    {
      id: 'india',
      label: 'India',
      status: 'Built for',
      detail: 'Tier 2 and Tier 3 cities nationwide',
    },
    {
      id: 'global',
      label: 'International markets',
      status: 'Designed for',
      detail: 'Multi-city, multi-currency architecture',
    },
  ],
};

/* -------------------------------------------------------------------------- */
/* Success stories — ONLY figures already published and verified on the site. */
/* Source: previous StatsBar / ClientsSection / TrustSection. See audit §9.   */
/* -------------------------------------------------------------------------- */

export interface Metric {
  value: number;
  prefix?: string;
  suffix: string;
  label: string;
  detail: string;
}

export const metrics: Metric[] = [
  {
    value: 22,
    suffix: '+',
    label: 'Cities Operational',
    detail: 'Across Tamil Nadu, with expansion underway',
  },
  { value: 5500, suffix: '+', label: 'Trips Every Day', detail: 'Live bookings processed daily' },
  { value: 3, suffix: 'M+', label: 'Successful Trips', detail: 'Completed on the platform' },
  {
    value: 6,
    suffix: '',
    label: 'Partner Companies',
    detail: 'Established operators growing with MOVIO',
  },
];

export const partners = [
  'Ftaxi',
  'Friendstrack',
  'Maya',
  'Ktaxi',
  'Apple Call Taxi',
  'Rahul Travels',
];

export const testimonials = [
  {
    name: 'Sathish',
    role: 'Founder & CEO, FTaxi',
    location: 'Thanjavur, Tamil Nadu',
    metric: '8 Cities · 70K+ Monthly Trips',
    before: '~11,000 monthly trips as a franchise',
    after: '70,000+ monthly trips under its own brand',
    quote:
      'With a vision to build our own identity, we launched FTaxi using the Movio platform. Today, FTaxi has expanded across 8 cities, and our monthly trips have grown from around 11,000 to over 70,000.',
  },
  {
    name: 'Rajesh',
    role: 'Founder, Maya Call Taxi',
    location: 'Vellore, Tamil Nadu',
    metric: '4 New Cities · 1 New State',
    before: 'Operating in 6 cities',
    after: '+4 cities and Puducherry in three months',
    quote:
      'After implementing Movio, our operations became more efficient, enabling us to expand to four new cities while also launching our services in a new state, Puducherry, within just three months.',
  },
  {
    name: 'Ganesh',
    role: 'Franchise Owner, FTaxi',
    location: 'Kovilpatti, Tamil Nadu',
    metric: '10X Turnover in 9 Months',
    before: 'A stable local franchise',
    after: '10X turnover in nine months',
    quote:
      'With Movio, we gained better control over our bookings and day-to-day operations, helping us achieve a remarkable 10X increase in turnover in just nine months.',
  },
];

/* -------------------------------------------------------------------------- */
/* Final CTA                                                                   */
/* -------------------------------------------------------------------------- */

export const partnerCta = {
  headline: ['Have an Established Business?', 'Let’s Build the Technology Around It.'],
  body: 'Tell us about your market, fleet or service. We will show you what MOVIO can launch for you.',
};

/* -------------------------------------------------------------------------- */
/* Navigation                                                                  */
/* -------------------------------------------------------------------------- */

export const solutionLinks = [
  { label: 'Ride Hailing Platform', href: '/products/ride-hailing', icon: 'TruckIcon' },
  { label: 'Logistics Platform', href: '/products/logistics', icon: 'ArrowPathIcon' },
  { label: 'Facility Pro', href: '/products/facility-pro', icon: 'HomeModernIcon' },
  { label: 'Delivery Services', href: '/products/courier', icon: 'BoltIcon' },
  { label: 'AI Based Call Center', href: '/products/ai-call-center', icon: 'PhoneIcon' },
  { label: 'E-Commerce & Retail', href: '/products/ecommerce', icon: 'ShoppingBagIcon' },
  { label: 'Ellan CPaaS', href: '/products/ellan-cpaas', icon: 'ChatBubbleLeftRightIcon' },
];

export const primaryNav = [
  { label: 'Solutions', href: '/products', dropdown: solutionLinks },
  { label: 'How MOVIO Works', href: '/homepage#movio-idea' },
  { label: 'Success Stories', href: '/homepage#success-stories' },
  { label: 'Ecosystem', href: '/homepage#ecosystem' },
  { label: 'About', href: '/about' },
];

/** Secondary pages kept reachable from the mobile menu (desktop reaches them via the footer). */
export const secondaryNav = [
  { label: 'Pricing', href: '/pricing' },
  { label: 'Blog', href: '/blog' },
  { label: 'Careers', href: '/careers' },
  { label: 'Contact', href: '/contact' },
  { label: 'Book a Demo', href: '/demo-booking' },
];
